import ExcelJS from "exceljs";
import axios from "axios";
import Swal from "sweetalert2";
import { PRODUCT_TYPES } from "../../../shared/enums/productTypeList";

// ✅ Validation function
const validateExcelData = (data, setExcelLoading, productTypes) => {
  let isValid = true;
  let error = "";
  // add productType here
  if (!data || data.length === 0) {
    return { isValid: false, error: "No data found in the Excel file." };
  }

  const requiredColumns = [
    "Store Name",
    "Store Owner Name",
    "Store Owner Contact Number",
    "Address",
    "City",
    "State",
    "PinCode",
    "Zone",
    // "Product Type",
    // "Width",
    // "Height",
    // "Thickness",
  ];

  if (!data[0]) {
    return {
      isValid: false,
      error: "Excel file is empty or has no valid rows.",
    };
  }

  // ✅ NEW: Map for uniqueness check
  const storeMap = new Map();

  // ✅ same store + different address -> must have different phone number validation

  const differentLocationMap = new Map();
  const isBlank = (v) => v == null || String(v).trim() === "";

  data.forEach((row, index) => {
    const rowNumber = index + 2;

    const userFilledRow = requiredColumns
      .filter((field) => field !== "Zone")
      .some((field) => !isBlank(row[field]));
    if (!userFilledRow) return;

    const stateFilled = !isBlank(row["State"]);

    requiredColumns.forEach((field) => {
      if (field === "Zone" && !stateFilled) return;

      if (isBlank(row[field])) {
        isValid = false;
        error = `Missing or invalid value for '${field}' at row ${rowNumber}.`;
        setExcelLoading(false);
      }

      if (!row[field] || row[field].toString().trim() === "") {
        isValid = false;
        error = `Missing or invalid value for '${field}' at row ${index + 2}.`;
        setExcelLoading(false);
      }
    });

    // ✅ Check Product Type is valid
    // if (
    //   row["Product Type"] &&
    //   !productTypes.includes(row["Product Type"].trim())
    //   // ||
    //   // !PRODUCT_TYPES.includes(row["Product Type"].trim())
    // ) {
    //   isValid = false;
    //   error = `Invalid Product Type '${row["Product Type"]}' at row ${
    //     index + 2
    //   }. Please use a valid product type.`;
    //   setExcelLoading(false);
    // }

    // ✅ NEW: Same Store + Address → same phone validation

    const storeName = row["Store Name"]?.trim().toLowerCase();
    const address = row["Address"]?.trim().toLowerCase();
    const contactNumber = row["Store Owner Contact Number"]?.toString().trim();
    const city = row["City"]?.trim().toLowerCase();

    const key = `${storeName}-${address}`;

    if (storeMap.has(key)) {
      const existPhone = storeMap.get(key);
      if (existPhone !== contactNumber) {
        isValid = false;
        error = `Same Store & Address found with different phone numbers at row ${rowNumber}.`;
        setExcelLoading(false);
      }
    } else {
      storeMap.set(key, contactNumber);
    }

    const locationKey = `${storeName}-${address}-${city}`;

    if (!differentLocationMap.has(storeName)) {
      differentLocationMap.set(storeName, []);
    }

    const existingEntries = differentLocationMap.get(storeName);

    // Find same phone used in different location
    const duplicatePhoneInDifferentLocation = existingEntries.find(
      (entry) =>
        entry.contactNumber === contactNumber &&
        entry.locationKey !== locationKey,
    );

    // ❌ Same store name + different location + same phone
    if (duplicatePhoneInDifferentLocation) {
      isValid = false;

      error = `Same Store Name with different location cannot have same phone number at row ${rowNumber}.`;

      setExcelLoading(false);
    } else {
      // Save current entry
      existingEntries.push({
        locationKey,
        contactNumber,
      });
    }
  });

  if (!isValid) {
    setExcelLoading(false);
    return { isValid: false, error };
  }

  return { isValid: true };
};

// ✅ Main function
export const handleFileUploadForLocations = async (
  e,
  setExcelLoading,
  setSnackbar,
  saveExcel = false,
  productTypes = [],
) => {
  const file = e?.target?.files[0];

  if (file.size > 2 * 1024 * 1024) {
    setSnackbar({
      open: true,
      message: "Maximum Limit Exceed (2 MB)",
      severity: "error",
    });
  }
  const formData = new FormData();
  formData.append("file", file);

  const readExcelFile = async (file) => {
    const reader = new FileReader();

    return new Promise((resolve, reject) => {
      reader.onload = async (event) => {
        const buffer = event.target.result;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);

        const worksheet = workbook.worksheets[0]; // first sheet
        const rows = [];

        // Zone is a formula cell, so ExcelJS returns { formula, result } instead of text
        const normalizeCellValue = (value) => {
          if (value == null) return "";
          if (typeof value === "object" && !(value instanceof Date)) {
            if ("formula" in value || "sharedFormula" in value) {
              return normalizeCellValue(value.result ?? "");
            }
            if (Array.isArray(value.richText)) {
              return value.richText.map((t) => t.text).join("");
            }
            if ("text" in value) return normalizeCellValue(value.text);
            return "";
          }
          return value;
        };

        worksheet.eachRow((row) => {
          const rowValues = row.values.slice(1).map(normalizeCellValue);
          rows.push(rowValues);
        });

        resolve(rows);
      };

      reader.onerror = (error) => reject(error);

      reader.readAsArrayBuffer(file);
    });
  };

  const processExcelDataToJson = (rows) => {
    const headers = rows[0];
    const data = rows.slice(1).map((row) => {
      const rowData = {};
      row.forEach((value, index) => {
        rowData[headers[index]] = value;
      });
      return rowData;
    });
    return data;
  };

  try {
    setExcelLoading(true);

    const rows = await new Promise((resolve) => {
      setTimeout(async () => {
        const r = await readExcelFile(file);
        resolve(r);
      }, 0);
    });

    const jsonData = processExcelDataToJson(rows);

    // ✅ Validate
    const validationResult = validateExcelData(
      jsonData,
      setExcelLoading,
      productTypes,
    );
    if (!validationResult.isValid) {
      Swal.fire({
        customClass: {
          container: "my-swal",
        },
        icon: "error",
        title: "Validation Error",
        text: validationResult.error,
        confirmButtonText: "OK",
      });
      return;
    }

    // ✅ Upload file to server
    const response = await axios.post(
      `${process.env.REACT_APP_URL}/upload-template/createScopeOfWork?saveExcel=${saveExcel}`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      },
    );

    if (response && response.data && response?.data?.success) {
      setSnackbar({
        open: true,
        message: "File Uploaded Successfully",
        severity: "success",
      });
      setExcelLoading(false);
      return response.data;
    } else {
      setExcelLoading(false);
      Swal.fire({
        customClass: {
          container: "my-swal",
        },
        icon: "error",
        title: "Upload Failed",
        text:
          response?.data?.message || "Server did not return a success flag.",
        confirmButtonText: "OK",
      });
    }
  } catch (error) {
    setExcelLoading(false);
    Swal.fire({
      customClass: {
        container: "my-swal",
      },
      icon: "error",
      title: "There was an error uploading the file.",
      text: error.message || error,
      confirmButtonText: "OK",
    });
  } finally {
    setExcelLoading(false);
  }
};
